import { AllQueryParam } from "polyrouter/allQueryParams";
import { all, any, needs, needsAny, Requirement } from "rbac";
import AllRolePermissions from "roles";
import {
  createReducer,
  createStandardAction as action
} from "typesafe-actions";
import { LocalKeys } from "utilities/i18n";

export type MenuActionType = "SELECT_MENU_ITEM";

export const selectMenuItem = action("SELECT_MENU_ITEM")<string>();

//export type MenuAction = ActionType<typeof selectMenuItem>;

export interface MenuItem {
  readonly id: string;
  readonly icon?: string;
  readonly kind: "simple" | "group" | "root";
  readonly name: LocalKeys;
  readonly url: string;
  readonly optimizely?: string;
  readonly subMenus: Array<MenuItem>;
  readonly persist?: Array<AllQueryParam>;
  readonly permission: Array<AllRolePermissions> | Requirement;
}

export interface MenuState {
  readonly items: Array<MenuItem>;
  readonly selectedId: string;
}

export function simpleItem(
  id: string,
  name: LocalKeys,
  permission: Array<AllRolePermissions> | Requirement,
  icon?: string,
  optimizely?: string,
  persist?: Array<AllQueryParam>
): MenuItem {
  return {
    id: id,
    icon: icon,
    kind: "simple",
    name: name,
    url: `/${id}`,
    persist: persist,
    optimizely: optimizely,
    subMenus: [],
    permission: permission
  };
}

function groupedItems(subMenus: Array<MenuItem>): MenuItem {
  const permissions = subMenus.map(x => x.permission);
  const permissionsAsStrings: Array<Array<string>> = permissions.filter(
    x => Array.isArray(x) && x.length > 0
  ) as Array<Array<string>>;
  const permissionsAsRequirements: Array<Requirement> = permissions.filter(
    x => typeof x === "function"
  ) as Array<Requirement>;
  const combinedPermissions = permissionsAsRequirements.concat(
    permissionsAsStrings.map(x => needsAny(x))
  );

  return {
    id: "",
    icon: "",
    kind: "group",
    name: "",
    url: "",
    subMenus,
    permission: any(combinedPermissions)
  };
}

const readTenantAndSites = all([needs("READ_TENANT"), needs("READ_SITES")]);
const readTenantAndDevice = all([needs("READ_TENANT"), needs("read:device")]);

export const defaultMenuState: MenuState = {
  selectedId: "home",
  items: [
    {
      id: "manage",
      kind: "root",
      name: "MANAGE",
      url: "/manage",
      permission: [],
      subMenus: [
        groupedItems([
          simpleItem(
            "manage/inventory",
            "INVENTORY",
            readTenantAndDevice,
            "inventory"
          ),
          simpleItem(
            "manage/actions",
            "ACTIONS",
            readTenantAndDevice,
            "actions"
          ),
          simpleItem(
            "manage/policy",
            "POLICY",
            readTenantAndSites,
            "manage_policies"
          ),
          simpleItem(
            "manage/licensing",
            "LICENSING",
            ["get:polypluslicense", "license:ReadTenant"],
            "manage_licensing"
          ),
          simpleItem(
            "manage/resource-files",
            "RESOURCE_FILES",
            ["read:device:resource:file"],
            "resource_file",
            "resource_files"
          )
        ]),
        groupedItems([
          simpleItem(
            "manage/sites",
            "SITES",
            readTenantAndSites,
            "manage_sites"
          ),
          simpleItem(
            "manage/rooms",
            "ROOMS",
            readTenantAndSites,
            "manage_rooms"
          ),
          simpleItem(
            "manage/device-groups",
            "DEVICE_GROUP_other",
            readTenantAndSites,
            "manage_device_groups"
          ),
          simpleItem(
            "manage/device-users",
            "DEVICE_USERS",
            ["READ_USERS"],
            "manage_device_users"
          )
        ]),
        groupedItems([
          simpleItem(
            "manage/register",
            "REGISTER_DEVICE",
            ["register:device"],
            "manage_register_device",
            undefined,
            undefined
          )
        ])
      ]
    },
    {
      id: "insights",
      kind: "root",
      name: "INSIGHTS",
      url: "/insights",
      permission: [],
      subMenus: [
        simpleItem(
          "insights/priority",
          "PRIORITY",
          ["GET_EPISODES"],
          "insights_priority",
          undefined,
          ["InsightsFilterState"]
        ),
        simpleItem(
          "insights/categories",
          "CATEGORIES",
          ["GET_EPISODES"],
          "insights_categories",
          undefined,
          ["InsightsFilterState"]
        )
      ]
    }
  ]
};

export const MenuReducer = createReducer(defaultMenuState).handleAction(
  selectMenuItem,
  (state, action) => {
    const doesAnyItemMatch = (m: MenuItem) => {
      return m.id === action.payload || m.subMenus.some(doesAnyItemMatch);
    };
    const anyMatch = state.items.some(doesAnyItemMatch);
    if (!anyMatch) {
      return state;
    }
    return {
      ...state,
      selectedId: action.payload
    };
  }
);
export default MenuReducer;
